import { Text } from '@change/ds-core-ui';
import { CheckIcon, InsightsIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { translationKey } from '@change-corgi/core/i18n';
import { Translate, TypedTranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { StepActions } from '#src/app/pages/startAPetitionV2/shared/components';
import { Callout, CalloutBody } from '#src/app/shared/components/callout';

import { ErrorScreen } from './components/ErrorScreen';
import { Loading } from './components/Loading';
import { usePrePublish } from './hooks/usePrePublish';

const PRIMARY_ACTION_LABEL_AUTHENTICATED = translationKey(
	'corgi.start_a_petition.prepublish.step_actions.button.create_petition',
);
type ChecklistState = 'complete' | 'incomplete';
type LoginState = ReturnType<typeof usePrePublish>['data']['loginState'];

function getLoginState(loginState: LoginState): ChecklistState {
	return loginState === 'AUTHENTICATED' ? 'complete' : 'incomplete';
}

const CHECKLIST = [
	{
		text: translationKey('corgi.start_a_petition.prepublish.checklist.item_1.text'),
		label: translationKey('corgi.start_a_petition.prepublish.checklist.item_1.label'),
		completedLabel: translationKey('corgi.start_a_petition.prepublish.checklist.item_1.completed_label'),
		state: getLoginState,
	},
	{
		text: translationKey('corgi.start_a_petition.prepublish.checklist.item_2.text'),
		label: translationKey('corgi.start_a_petition.prepublish.checklist.item_2.label'),
		completedLabel: undefined,
		state: () => 'incomplete' as const,
	},
	{
		text: translationKey('corgi.start_a_petition.prepublish.checklist.item_3.text'),
		label: translationKey('corgi.start_a_petition.prepublish.checklist.item_3.label'),
		completedLabel: undefined,
		state: () => 'incomplete' as const,
	},
] as const;

const STATE_STYLES: Record<ChecklistState, string> = {
	complete: 'border-stroke-disabled text-typography-disabled',
	incomplete: 'bg-fill-inverse-strong text-typography-strong border-stroke-info-strong',
};
export const PrePublish: React.FC = () => {
	const { typedTranslate } = useI18n();
	const { data, actions } = usePrePublish();

	if (data.error) return <ErrorScreen error={data.error} />;
	if (data.isLoading) return <Loading />;

	return (
		// eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname
		<div className="container mx-auto mb-[88px] flex max-w-[620px] flex-col gap-6 px-5 py-8 md:gap-12">
			<div className="grid gap-8">
				<Text as="h1" variant="heading" className="m-0">
					<Translate value="corgi.start_a_petition.prepublish.heading" />
				</Text>

				<div className="grid gap-6">
					<Text as="p" variant="body" size="large">
						<Translate value="corgi.start_a_petition.prepublish.description" />
					</Text>
					{/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
					<ol role="list" className="grid gap-6">
						{CHECKLIST.map((item, index: number) => {
							const state = item.state(data.loginState);
							return (
								// eslint-disable-next-line jsx-a11y/no-redundant-roles
								<li
									role="listitem"
									aria-label={typedTranslate(item.label)}
									className="grid grid-cols-[min-content_1fr] items-center justify-center gap-4"
									key={`prepublish-${index + 1}`}
								>
									<div
										aria-hidden="true"
										className={cn([
											'flex h-8 w-8 items-center justify-center rounded-full border-2 border-solid',
											STATE_STYLES[state],
										])}
									>
										{state === 'complete' ? <CheckIcon /> : index + 1}
									</div>
									<div>
										<Text variant="body" size="large">
											{state === 'complete' ? (
												<s className="text-typography-weak">
													<TypedTranslateHtml value={item.text} />
												</s>
											) : (
												<TypedTranslateHtml value={item.text} />
											)}
										</Text>
									</div>
								</li>
							);
						})}
					</ol>
				</div>

				<Callout icon={<InsightsIcon />}>
					<CalloutBody>
						<Translate value="corgi.start_a_petition.prepublish.callout.text" />
					</CalloutBody>
				</Callout>
			</div>

			<StepActions
				type="navigation"
				onSecondaryAction={actions.back}
				onPrimaryAction={actions.submit}
				primaryActionLabel={PRIMARY_ACTION_LABEL_AUTHENTICATED} // todo handle unauthenticated label
			/>
		</div>
	);
};
